import React, { useState, useEffect } from 'react';
import { 
  Menu, 
  X, 
  Bell, 
  User, 
  Users, 
  ChevronRight,
  ChevronLeft,
  Settings,
  LogOut,
  ChevronDown,
  Package,
  Store,
  MessageSquare,
  BarChart3,
  Activity,
  Stamp,
  Coins,
  Gift,
  TrendingUp,
  UserPlus,
  Award,
  AlertCircle,
  Plus,
  Edit2,
  ArrowUp,
  ArrowDown,
  Ticket
} from 'lucide-react';

interface UserInfo {
  accountId: string;
  userName: string;
  userId: string;
  role: string;
}

interface ProjectDetail {
  id: string;
  code: string;
  name: string;
  description: string;
  status: string;
  createdAt: string;
}

interface MembershipConfig {
  isActive: boolean;
  benefitType: 'stamp' | 'point' | 'both';
  stampConfig?: {
    requiredCount: number;
    rewardType: string;
    rewardValue: string;
  };
  pointConfig?: {
    earnRate: number;
    minPurchase: number;
  };
}

interface StoreStats {
  storeCode: string;
  storeName: string;
  memberCount: number;
  growthRate: number;
}

interface ProductBenefit {
  productCode: string;
  productName: string;
  benefitType: string;
  benefitValue: number;
}

const MembershipDashboard_old: React.FC = () => {
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState<boolean>(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState<boolean>(false);
  const [activeMenu, setActiveMenu] = useState<string>('membership');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('7days');

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const userInfo: UserInfo = {
    accountId: 'ACC-123456789',
    userName: '홍길동',
    userId: 'admin@orderflow.com',
    role: '루트 관리자'
  };

  const projectDetail: ProjectDetail = {
    id: 'PRJ-001',
    code: 'GANGNAM-001',
    name: '강남점',
    description: '강남역 1번 출구 플래그십 스토어',
    status: 'ACTIVE',
    createdAt: '2025-01-10'
  };

  // Mock membership configuration
  const membershipConfig: MembershipConfig = {
    isActive: true,
    benefitType: 'both',
    stampConfig: {
      requiredCount: 10,
      rewardType: '무료음료쿠폰',
      rewardValue: '아메리카노 1잔'
    },
    pointConfig: {
      earnRate: 5,
      minPurchase: 1000
    }
  };

  // Mock statistics
  const totalMembers = 12543;
  const activeStores = 7;
  const todayNewMembers = 45;
  const monthGrowth = 8.5;

  const topStores: StoreStats[] = [
    { storeCode: 'STORE-001', storeName: '강남점', memberCount: 3245, growthRate: 12.3 },
    { storeCode: 'STORE-002', storeName: '삼성점', memberCount: 2890, growthRate: 8.7 },
    { storeCode: 'STORE-004', storeName: '역삼점', memberCount: 2456, growthRate: 15.2 },
    { storeCode: 'STORE-006', storeName: '청담점', memberCount: 1987, growthRate: -2.3 },
    { storeCode: 'STORE-007', storeName: '논현점', memberCount: 1965, growthRate: 6.8 }
  ];

  const benefitProducts: ProductBenefit[] = [
    { productCode: 'PROD-001', productName: '아메리카노', benefitType: 'stamp', benefitValue: 1 },
    { productCode: 'PROD-002', productName: '카페라떼', benefitType: 'stamp', benefitValue: 1 },
    { productCode: 'PROD-004', productName: '초콜릿 케이크', benefitType: 'stamp', benefitValue: 2 },
    { productCode: 'PROD-005', productName: '크로와상', benefitType: 'point', benefitValue: 10 }
  ];

  const dailyMembers = [
    { date: '01-14', count: 45 },
    { date: '01-15', count: 38 },
    { date: '01-16', count: 52 },
    { date: '01-17', count: 41 },
    { date: '01-18', count: 68 },
    { date: '01-19', count: 55 },
    { date: '01-20', count: 45 }
  ];

  const genderRatio = { male: 42, female: 58 };

  const getStatusBadge = (status: string) => {
    if (status === 'ACTIVE') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <span className="w-1.5 h-1.5 mr-1 bg-green-400 rounded-full"></span>
          활성
        </span>
      );
    }
    return null;
  };

  const menuItems = [
    { id: 'overview', label: '개요', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'services', label: '서비스 관리', icon: <Activity className="w-5 h-5" /> },
    { id: 'stores', label: '매장 관리', icon: <Store className="w-5 h-5" /> },
    { id: 'products', label: '상품 관리', icon: <Package className="w-5 h-5" /> },
    { id: 'vouchers', label: '교환권 관리', icon: <Ticket className="w-5 h-5" /> },
    { id: 'membership', label: '멤버십 관리', icon: <Users className="w-5 h-5" /> },
    { id: 'members', label: '회원 관리', icon: <User className="w-5 h-5" /> },
    { id: 'qna', label: '1:1 문의', icon: <MessageSquare className="w-5 h-5" /> },
    { id: 'settings', label: '프로젝트 설정', icon: <Settings className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <aside className={`
        hidden lg:flex
        ${isDesktopSidebarOpen ? 'w-64' : 'w-16'}
        bg-white border-r border-gray-200 transition-all duration-300 flex-col relative
      `}>
        <div className="p-4 border-b border-gray-200">
          {isDesktopSidebarOpen ? (
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{projectDetail.name}</h3>
                <div className="mt-1">
                  {getStatusBadge(projectDetail.status)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-300 rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
        >
          {isDesktopSidebarOpen ? (
            <ChevronLeft className="w-3 h-3 text-gray-600" />
          ) : (
            <ChevronRight className="w-3 h-3 text-gray-600" />
          )}
        </button>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveMenu(item.id)}
                  className={`w-full flex items-center ${
                    isDesktopSidebarOpen ? 'px-3 py-2' : 'p-2 justify-center'
                  } rounded-lg transition-colors ${
                    activeMenu === item.id
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={!isDesktopSidebarOpen ? item.label : undefined}
                >
                  {item.icon}
                  {isDesktopSidebarOpen && <span className="ml-3">{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {isDesktopSidebarOpen && (
          <div className="p-4 border-t border-gray-200">
            <button className="w-full text-sm text-gray-600 hover:text-gray-900 flex items-center">
              <ChevronLeft className="w-4 h-4 mr-1" />
              프로젝트 목록으로
            </button>
          </div>
        )}
      </aside>

      <aside className={`
        fixed lg:hidden inset-y-0 left-0 z-50
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        w-64 bg-white border-r border-gray-200 transition-all duration-300 flex flex-col
      `}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{projectDetail.name}</h3>
                <div className="mt-1">
                  {getStatusBadge(projectDetail.status)}
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => {
                    setActiveMenu(item.id);
                    setIsMobileSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeMenu === item.id
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {item.icon}
                  <span className="ml-3">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button className="w-full text-sm text-gray-600 hover:text-gray-900 flex items-center">
            <ChevronLeft className="w-4 h-4 mr-1" />
            프로젝트 목록으로
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center lg:hidden">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center ml-2 mr-3">
                <Store className="w-4 h-4 text-white" />
              </div>
            </div>
            
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">멤버십 관리</h1>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            <div className="relative">
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center space-x-2 sm:space-x-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{userInfo.userName}</p>
                  <p className="text-xs text-gray-500">{userInfo.role}</p>
                </div>
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
                <ChevronDown className="w-4 h-4 text-gray-600 hidden sm:block" />
              </button>

              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{userInfo.userId}</p>
                    <p className="text-xs text-gray-500">{userInfo.accountId}</p>
                  </div>
                  <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    계정 설정
                  </button>
                  <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                    <LogOut className="w-4 h-4 mr-2" />
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Membership Configuration */}
            {!membershipConfig.isActive ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">멤버십이 설정되지 않았습니다</h2>
                <p className="text-gray-600 mb-6">멤버십 혜택을 설정하여 고객에게 더 나은 서비스를 제공하세요.</p>
                <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center">
                  <Plus className="w-5 h-5 mr-2" />
                  멤버십 설정하기
                </button>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Award className="w-5 h-5 mr-2" />
                      멤버십 혜택 설정
                    </h2>
                    <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center">
                      <Edit2 className="w-4 h-4 mr-1" />
                      수정
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {membershipConfig.benefitType === 'stamp' || membershipConfig.benefitType === 'both' ? (
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center mb-3">
                          <Stamp className="w-5 h-5 text-blue-600 mr-2" />
                          <h3 className="font-medium text-gray-900">스탬프 적립</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {membershipConfig.stampConfig?.requiredCount}개 적립 시
                        </p>
                        <p className="text-sm font-medium text-blue-600">
                          {membershipConfig.stampConfig?.rewardType}: {membershipConfig.stampConfig?.rewardValue}
                        </p>
                      </div>
                    ) : null}

                    {membershipConfig.benefitType === 'point' || membershipConfig.benefitType === 'both' ? (
                      <div className="p-4 bg-green-50 rounded-lg">
                        <div className="flex items-center mb-3">
                          <Coins className="w-5 h-5 text-green-600 mr-2" />
                          <h3 className="font-medium text-gray-900">포인트 적립</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          결제 금액의 {membershipConfig.pointConfig?.earnRate}%
                        </p>
                        <p className="text-sm text-gray-500">
                          최소 구매: {membershipConfig.pointConfig?.minPurchase?.toLocaleString()}원
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">총 회원수</span>
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{totalMembers.toLocaleString()}</p>
                    <div className="flex items-center mt-2 text-sm">
                      <ArrowUp className="w-3 h-3 text-green-500 mr-1" />
                      <span className="text-green-600">{monthGrowth}%</span>
                      <span className="text-gray-500 ml-1">지난달 대비</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">활성 매장</span>
                      <Store className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{activeStores}</p>
                    <p className="text-sm text-gray-500 mt-2">멤버십 사용 중</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">오늘 신규</span>
                      <UserPlus className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">+{todayNewMembers}</p>
                    <p className="text-sm text-gray-500 mt-2">신규 가입자</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">성비</span>
                      <Users className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex items-center mt-1">
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span>남성</span>
                          <span>{genderRatio.male}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${genderRatio.male}%` }} />
                        </div>
                      </div>
                      <div className="mx-2 text-gray-400">/</div>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span>여성</span>
                          <span>{genderRatio.female}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-pink-600 h-2 rounded-full" style={{ width: `${genderRatio.female}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Daily Members Chart */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold text-gray-900">일별 회원 가입</h3>
                      <select 
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="7days">7일</option>
                        <option value="30days">30일</option>
                        <option value="90days">90일</option>
                      </select>
                    </div>
                    <div className="space-y-3">
                      {dailyMembers.map((day) => (
                        <div key={day.date} className="flex items-center">
                          <span className="text-xs text-gray-500 w-12">{day.date}</span>
                          <div className="flex-1 mx-3">
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div 
                                className="bg-blue-600 h-4 rounded-full flex items-center justify-end px-2"
                                style={{ width: `${(day.count / 70) * 100}%` }}
                              >
                                <span className="text-xs text-white">{day.count}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Stores */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">매장별 회원수</h3>
                    <div className="space-y-3">
                      {topStores.map((store, index) => (
                        <div key={store.storeCode} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-500 w-6">{index + 1}</span>
                            <span className="text-sm text-gray-900 ml-2">{store.storeName}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900 mr-2">
                              {store.memberCount.toLocaleString()}
                            </span>
                            <span className={`text-xs flex items-center ${
                              store.growthRate > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {store.growthRate > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                              {Math.abs(store.growthRate)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Benefit Products */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">혜택 적용 상품</h3>
                    <div className="space-y-3">
                      {benefitProducts.map((product) => (
                        <div key={product.productCode} className="flex items-center justify-between">
                          <span className="text-sm text-gray-900">{product.productName}</span>
                          <div className="flex items-center">
                            {product.benefitType === 'stamp' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <Stamp className="w-3 h-3 mr-1" />
                                스탬프 {product.benefitValue}개
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <Coins className="w-3 h-3 mr-1" />
                                포인트 {product.benefitValue}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      <button className="w-full text-sm text-blue-600 hover:text-blue-700 text-center pt-2 border-t">
                        전체 상품 보기
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MembershipDashboard_old;